/**
 * System metrics + workspace resource-budget schemas — Phase 12 (M10).
 *
 * Mirrors:
 *   - GET /api/system/load                  (existed pre-Phase-12)
 *   - GET /api/system/sessions/:id/load     (new)
 *   - GET /api/system/sessions/active       (new)
 *   - GET /api/system/sessions/estimate     (new)
 *   - GET /api/workspaces/me/budget         (new)
 *   - PATCH /api/workspaces/me/budget       (new)
 */
import { z } from 'zod';

// ── Host load (already shipped pre-12; kept here for completeness) ──
export const systemLoadSchema = z.object({
  ram: z.object({
    usedPct: z.number().nullable(),
    totalBytes: z.number().nullable(),
  }),
  cpu: z.object({
    usedPct: z.number().nullable(),
  }),
  disk: z.object({
    usedPct: z.number().nullable(),
    totalBytes: z.number().nullable(),
  }),
  fetchedAt: z.string().datetime(),
});
export type SystemLoad = z.infer<typeof systemLoadSchema>;

// ── AI subscription usage windows — GET /api/system/ai-usage ────────
// Mirrors the Anthropic OAuth-usage + ChatGPT/Codex subscription windows
// surfaced in the dashboard ServerLoadMonitor (settings-v2). `pct` is 0-100
// utilization (nullable for graceful "no data", same strategy as
// systemLoadSchema). `resetsAt` is the ISO-8601 window-reset time.
export const usageWindowSchema = z.object({
  pct: z.number().nullable(),
  resetsAt: z.string().nullable(),
});
export type UsageWindow = z.infer<typeof usageWindowSchema>;

export const claudeUsageSchema = z.object({
  fiveHour: usageWindowSchema,
  weekly: usageWindowSchema,
  weeklyOpus: usageWindowSchema.nullable(),
  weeklySonnet: usageWindowSchema.nullable(),
  /** true when the last poll failed and we are serving last-good data. */
  stale: z.boolean(),
});
export type ClaudeUsage = z.infer<typeof claudeUsageSchema>;

// Codex (ChatGPT subscription) usage — live as of Wave 1 (2026-06-20). The
// ChatGPT backend exposes the rate-limit windows at
// GET /backend-api/wham/usage; `fiveHour` mirrors rate_limit.primary_window,
// `weekly` mirrors rate_limit.secondary_window. Same nullable+stale strategy
// as ClaudeUsage. `codex` is null only when no successful poll has happened yet.
export const codexUsageSchema = z.object({
  fiveHour: usageWindowSchema,
  weekly: usageWindowSchema,
  /** true when the last poll failed and we are serving last-good data. */
  stale: z.boolean(),
});
export type CodexUsage = z.infer<typeof codexUsageSchema>;

export const aiUsageSchema = z.object({
  claude: claudeUsageSchema.nullable(),
  codex: codexUsageSchema.nullable(),
  fetchedAt: z.string().datetime(),
});
export type AiUsage = z.infer<typeof aiUsageSchema>;

// ── Per-session live load (cAdvisor per-container) ──────────────────
// `ram.usedMb` is bytes / 1_048_576 rounded; null when the container
// isn't reporting yet. Same nullable strategy as the host load — if
// Prometheus/cAdvisor is unreachable the UI degrades to placeholders.
export const sessionLoadSchema = z.object({
  sessionId: z.string().uuid(),
  state: z.enum(['created', 'active', 'idle', 'sleeping', 'waking', 'dead']),
  ram: z.object({
    usedMb: z.number().int().nonnegative().nullable(),
    pctOfLimit: z.number().nullable(),
  }),
  cpu: z.object({
    usedPct: z.number().nullable(),
  }),
  disk: z.object({
    usedMb: z.number().int().nonnegative().nullable(),
    pctOfRetentionBudget: z.number().nullable(),
  }),
  network: z.object({
    bytesSent: z.number().int().nonnegative().nullable(),
    bytesRecv: z.number().int().nonnegative().nullable(),
  }),
  uptimeSeconds: z.number().int().nonnegative().nullable(),
  fetchedAt: z.string().datetime(),
});
export type SessionLoad = z.infer<typeof sessionLoadSchema>;

// ── Active sessions list (ActiveSessions widget) ────────────────────
export const activeSessionRowSchema = z.object({
  sessionId: z.string().uuid(),
  cardId: z.string().uuid(),
  cardTitle: z.string(),
  boardId: z.string().uuid(),
  workspaceId: z.string().uuid(),
  model: z.string(),
  state: z.enum(['active', 'idle']),
  uptimeSeconds: z.number().int().nonnegative(),
  ramMb: z.number().int().nonnegative().nullable(),
  cpuPct: z.number().nullable(),
});
export type ActiveSessionRow = z.infer<typeof activeSessionRowSchema>;

export const activeSessionsResponseSchema = z.object({
  sessions: z.array(activeSessionRowSchema),
  fetchedAt: z.string().datetime(),
});
export type ActiveSessionsResponse = z.infer<typeof activeSessionsResponseSchema>;

// ── Pre-launch estimate (Start session badge) ───────────────────────
// Caller supplies (model, columnKind?) — gateway returns a historical
// average + sample size + a `risk` label.
export const sessionEstimateInputSchema = z.object({
  model: z.string().min(1),
  columnId: z.string().uuid().optional(),
});
export type SessionEstimateInput = z.infer<typeof sessionEstimateInputSchema>;

export const sessionEstimateRiskSchema = z.enum(['ok', 'busy', 'critical']);
export type SessionEstimateRisk = z.infer<typeof sessionEstimateRiskSchema>;

export const sessionEstimateSchema = z.object({
  model: z.string(),
  ramMbAvg: z.number().int().nonnegative(),
  cpuPctAvg: z.number().nonnegative(),
  sampleSize: z.number().int().nonnegative(),
  /** `risk` is computed against host load + workspace budget. */
  risk: sessionEstimateRiskSchema,
  reason: z.string().nullable(),
});
export type SessionEstimate = z.infer<typeof sessionEstimateSchema>;

// ── Workspace resource budget ────────────────────────────────────────
export const resourceBudgetSchema = z.object({
  max_concurrent_active_sessions: z.number().int().min(1).max(50),
  auto_pause_oldest_when_at_cap: z.boolean(),
});
export type ResourceBudget = z.infer<typeof resourceBudgetSchema>;

export const updateResourceBudgetSchema = z.object({
  max_concurrent_active_sessions: z.number().int().min(1).max(50).optional(),
  auto_pause_oldest_when_at_cap: z.boolean().optional(),
});
export type UpdateResourceBudgetInput = z.infer<typeof updateResourceBudgetSchema>;
